import React, { useState, useEffect, useRef } from 'react';

const QuestionText = ({ text }) => {
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const [parsedContent, setParsedContent] = useState([]);
  const tooltipRefs = useRef({});
  const hoverTimeoutRef = useRef(null);

  // Parse text into React elements
  useEffect(() => {
    // Add this debug line
    console.log("Original text:", text);
    
    // Parse the text into tokens
    function parseText(inputText) {
      // First, handle tooltips
      const tooltipRegex = /\[tooltip:\s*([^\]]+)\]([^[]+)\[\/tooltip\]/g;
      let tooltipText = inputText;
      const tooltips = [];
      let match;
      
      while ((match = tooltipRegex.exec(inputText)) !== null) {
        const [fullMatch, explanation, term] = match;
        const tooltipId = `tooltip-${tooltips.length}`;
        tooltips.push({ id: tooltipId, term, explanation });
        tooltipText = tooltipText.replace(fullMatch, `###TOOLTIP-${tooltipId}###`);
      }
      
      console.log("After tooltip parsing:", tooltipText);
      
      // Use a much more flexible regex for links that handles spaces and both types of quotes
      const linkRegex = /<a\s+href\s*=\s*(['"])(.*?)\1.*?>([^<]*?)<\/a>/gi;
      let linkText = tooltipText;
      const links = [];
      
      // Debug existing regex
      const debugRegex = new RegExp(linkRegex);
      console.log("Link regex will match:", debugRegex.test(tooltipText));
      
      match = linkRegex.exec(tooltipText);
      while (match !== null) {
        console.log("Link match:", match);
        const [fullMatch, , href, linkContent] = match;
        const linkId = `link-${links.length}`;
        links.push({ id: linkId, href, content: linkContent });
        linkText = linkText.replace(fullMatch, `###LINK-${linkId}###`);
        match = linkRegex.exec(tooltipText);
      }
      
      console.log("After link parsing:", linkText);
      console.log("Found links:", links);
      
      // If the regex still isn't matching, use a fallback approach for the specific format
      if (links.length === 0 && tooltipText.includes("<a href")) {
        // Try a more specific pattern for your exact format
        const specialLinkRegex = /<a\s+href\s*=\s*'([^']*)'[^>]*>([^<]*)<\/a>/gi;
        match = specialLinkRegex.exec(tooltipText);
        while (match !== null) {
          console.log("Special link match:", match);
          const [fullMatch, href, linkContent] = match;
          const linkId = `link-${links.length}`;
          links.push({ id: linkId, href, content: linkContent });
          linkText = linkText.replace(fullMatch, `###LINK-${linkId}###`);
          match = specialLinkRegex.exec(tooltipText);
        }
        
        console.log("After special link parsing:", linkText);
        console.log("Found special links:", links);
      }
      
      // Split on placeholders and create elements
      const segments = linkText.split(/(###(?:TOOLTIP|LINK)-[^#]+###)/);
      console.log("Segments:", segments);
      
      const elements = segments.map((segment, index) => {
        // Check if this is a tooltip placeholder
        const tooltipMatch = segment.match(/^###TOOLTIP-([^#]+)###$/);
        if (tooltipMatch) {
          const tooltipId = tooltipMatch[1];
          const tooltip = tooltips.find(t => t.id === tooltipId);
          if (tooltip) {
            return (
              <span 
                key={`tooltip-${index}`}
                ref={el => tooltipRefs.current[tooltipId] = el}
                className="tooltip-term"
                data-tooltip-id={tooltipId}
                data-tooltip-content={tooltip.explanation}
                onMouseEnter={() => handleTooltipMouseEnter(tooltipId, tooltip.explanation)}
                onMouseLeave={handleTooltipMouseLeave}
              >
                {tooltip.term}
              </span>
            );
          }
        }
        
        // Check if this is a link placeholder
        const linkMatch = segment.match(/^###LINK-([^#]+)###$/);
        if (linkMatch) {
          const linkId = linkMatch[1];
          const link = links.find(l => l.id === linkId);
          if (link) {
            return (
              <a 
                key={`link-${index}`}
                href={link.href}
                className="question-link"
                onClick={handleLinkClick}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.content}
              </a>
            );
          }
        }
        
        // Regular text
        return <span key={`text-${index}`}>{segment}</span>;
      });
      
      return elements;
    }
    
    setParsedContent(parseText(text));
  }, [text]);
  
  // Link click handler - React event handler
  const handleLinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Link clicked:", e.currentTarget.href);
    window.open(e.currentTarget.href, '_blank', 'noopener,noreferrer');
  };
  
  // Tooltip handlers
  const handleTooltipMouseEnter = (tooltipId, content) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      const element = tooltipRefs.current[tooltipId];
      if (element) {
        const rect = element.getBoundingClientRect();
        setTooltip({
          visible: true,
          content: content,
          x: rect.left + window.scrollX + rect.width / 2,
          y: rect.top + window.scrollY - 10
        });
      }
    }, 400);
  };
  
  const handleTooltipMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setTooltip(prev => ({ ...prev, visible: false }));
    }, 100);
  };
  
  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isTooltipTerm = e.target.classList.contains('tooltip-term');
      if (!isTooltipTerm && tooltip.visible) {
        setTooltip(prev => ({ ...prev, visible: false }));
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [tooltip.visible]);
  
  return (
    <div className="relative">
      <h3 className="text-xl font-medium mb-6 leading-relaxed question-text">
        {parsedContent}
      </h3>
      
      {tooltip.visible && (
        <div
          className="fixed z-[1000] pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="relative">
            <div
              className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl max-w-xs text-sm leading-relaxed"
              style={{
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(4px)',
                animation: 'tooltipFadeIn 0.2s ease-out'
              }}
            >
              {tooltip.content}
            </div>
            <div
              className="absolute top-full left-1/2 transform -translate-x-1/2"
              style={{
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid #1f2937',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))'
              }}
            />
          </div>
        </div>
      )}
      
      <style jsx>{`
        .tooltip-term {
          cursor: help;
          border-bottom: 1px dotted #059669;
          color: #059669;
          position: relative;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .tooltip-term:hover {
          color: #047857;
          background-color: rgba(5, 150, 105, 0.1);
          padding: 1px 3px;
          margin: -1px -3px;
          border-radius: 3px;
          border-bottom: 1px solid #059669;
        }
        
        .question-link {
          color: #059669;
          text-decoration: underline;
          font-weight: 500;
          transition: color 0.2s ease;
          cursor: pointer;
          position: relative;
          z-index: 5;
          padding: 0 2px;
        }
        
        .question-link:hover {
          color: #047857;
          background-color: rgba(5, 150, 105, 0.1);
          text-decoration: none;
        }
        
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default QuestionText;